import api from '../../../api/client';
import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Steps,
  Upload,
  Row,
  Col,
  message,
  Spin,
  Radio,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { counties } from "../../../utils/counties";

const { Step } = Steps;
const { Dragger } = Upload;
const { Option } = Select;

const UserForm = ({ visible, onCancel, onSuccess, initialData, apiUrl }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [farmOptions, setFarmOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);
  const [userType, setUserType] = useState(
    initialData?.employee === 1 ? "employee" : "admin",
  );
  const account = useSelector((state) => state.account);

  // File states (only for employees)
  const [passportFile, setPassportFile] = useState(null);
  const [idFile, setIdFile] = useState(null);
  const [nhifFile, setNhifFile] = useState(null);
  const [nssfFile, setNssfFile] = useState(null);

  // Load options
  useEffect(() => {
    if (visible) {
      const fetchOptions = async () => {
        try {
          const [farmsRes, rolesRes, positionsRes] = await Promise.all([
            api.get(`/farm`),
            api.get(`/roles`),
            api.get(`/positions`),
          ]);
          setFarmOptions(
            farmsRes.data.map((f) => ({ value: f.id, label: f.name })),
          );
          setRoleOptions(
            rolesRes.data.map((r) => ({ value: r.id, label: r.name })),
          );
          setPositionOptions(
            positionsRes.data.map((p) => ({ value: p.id, label: p.name })),
          );
        } catch (error) {
          console.error("Failed to load options", error);
        }
      };
      fetchOptions();
    }
  }, [visible, apiUrl]);

  // Set initial form values
  useEffect(() => {
    if (initialData) {
      setUserType(initialData.employee === 1 ? "employee" : "admin");
      form.setFieldsValue({
        ...initialData,
        hireDate: initialData.hireDate ? dayjs(initialData.hireDate) : null,
        user_type: initialData.employee === 1 ? "employee" : "admin",
      });
    } else {
      form.resetFields();
      setUserType("admin");
      setCurrentStep(0);
      setPassportFile(null);
      setIdFile(null);
      setNhifFile(null);
      setNssfFile(null);
    }
  }, [initialData, form]);

  const handleUserTypeChange = (e) => {
    const newType = e.target.value;
    setUserType(newType);
    form.setFieldsValue({ user_type: newType });
    setCurrentStep(0); // reset steps when type changes
  };

  // Validate current step
  const validateStep = async () => {
    try {
      let fieldsToValidate = [];
      if (userType === "admin") {
        if (currentStep === 0) {
          fieldsToValidate = [
            "firstname",
            "lastname",
            "email",
            "phone_number",
            "id_number",
            "farm",
            "gender",
            "position",
          ];
        } else {
          fieldsToValidate = ["is_admin"];
        }
      } else {
        switch (currentStep) {
          case 0:
            fieldsToValidate = [
              "firstname",
              "lastname",
              "email",
              "phone_number",
              "id_number",
              "farm",
              "gender",
              "hireDate",
              "position",
            ];
            break;
          case 1:
            fieldsToValidate = [
              "nssf",
              "nhif",
              "county",
              "address",
              "leave_days",
              "payment_rate",
              "emp_status",
              "is_active",
            ];
            break;
          case 2:
            fieldsToValidate = [
              "nok_firstname",
              "nok_lastname",
              "nok_email",
              "nok_phone_number",
              "nok_address",
            ];
            break;
          case 3:
            if (!initialData && (!passportFile || !idFile)) {
              message.error("Passport photo and ID card are required");
              return false;
            }
            return true;
          default:
            return true;
        }
      }
      await form.validateFields(fieldsToValidate);
      return true;
    } catch (error) {
      message.error("Please fill all required fields");
      return false;
    }
  };

  const next = async () => {
    const isValid = await validateStep();
    if (isValid) setCurrentStep(currentStep + 1);
  };

  const prev = () => setCurrentStep(currentStep - 1);

  // const onFinish = async () => {
  //   const isValid = await validateStep();
  //   if (!isValid) return;
  //   setLoading(true);
  //   try {
  //     const values = form.getFieldsValue();
  //     const formData = new FormData();

  //     // Common fields
  //     formData.append("firstname", values.firstname);
  //     formData.append("lastname", values.lastname);
  //     formData.append("email", values.email);
  //     formData.append("phone_number", values.phone_number);
  //     formData.append("id_number", values.id_number);
  //     formData.append("mpesa_number", values.mpesa_number || "");
  //     formData.append("farm", values.farm);
  //     formData.append("employee", userType === "employee" ? 1 : 0); // 1=employee, 0=admin
  //     formData.append("is_admin", values.is_admin || ""); // role ID (optional for employees)
  //     formData.append("position", values.position || "");
  //     formData.append("gender", values.gender);
  //     formData.append("userName", account.user.firstname);
  //     formData.append("userId", account.user.id);

  //     if (initialData) formData.append("id", initialData.id);

  //     // Employee-specific fields
  //     if (userType === "employee") {
  //       formData.append("hireDate", values.hireDate?.format("YYYY-MM-DD"));
  //       formData.append("nssf", values.nssf);
  //       formData.append("nhif", values.nhif);
  //       formData.append("county", values.county);
  //       formData.append("address", values.address);
  //       formData.append("leave_days", values.leave_days);
  //       formData.append("payment_rate", values.payment_rate);
  //       formData.append("emp_status", values.emp_status);
  //       formData.append("is_active", values.is_active);
  //       formData.append("nok_firstname", values.nok_firstname);
  //       formData.append("nok_lastname", values.nok_lastname);
  //       formData.append("nok_email", values.nok_email);
  //       formData.append("nok_phone_number", values.nok_phone_number);
  //       formData.append("nok_address", values.nok_address);
  //       if (passportFile) formData.append("passport", passportFile);
  //       if (idFile) formData.append("id_card", idFile);
  //       if (nhifFile) formData.append("nhif_card", nhifFile);
  //       if (nssfFile) formData.append("nssf_card", nssfFile);
  //     }

  //     const url = initialData
  //       ? `/users/update/${initialData.id}`
  //       : `/users/new`;
  //     const method = initialData ? "put" : "post";
  //     const { data } = await axios[method](url, formData, {
  //       headers: { "Content-Type": "multipart/form-data" },
  //     });
  //     message.success(
  //       data.message ||
  //         `User ${initialData ? "updated" : "created"} successfully`,
  //     );
  //     onSuccess();
  //   } catch (error) {
  //     message.error(error.response?.data?.message || "Operation failed");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Inside UserForm component

  const onFinish = async () => {
    const isValid = await validateStep();
    if (!isValid) return;
    setLoading(true);
    try {
      const values = form.getFieldsValue(true); // get all fields, including those not touched

      const formData = new FormData();

      // Helper to append only non-undefined and non-empty string values (except for required fields)
      const appendIfValid = (key, value) => {
        if (value !== undefined && value !== null && value !== "") {
          // For dates, convert to string
          if (dayjs.isDayjs(value)) {
            formData.append(key, value.format("YYYY-MM-DD"));
          } else {
            formData.append(key, String(value));
          }
        }
      };

      // Common fields (always append if they have a value)
      appendIfValid("firstname", values.firstname);
      appendIfValid("lastname", values.lastname);
      appendIfValid("email", values.email);
      appendIfValid("phone_number", values.phone_number);
      appendIfValid("id_number", values.id_number);
      appendIfValid("mpesa_number", values.mpesa_number);
      appendIfValid("farm", values.farm);
      appendIfValid("employee", userType === "employee" ? "1" : "0");
      appendIfValid("is_admin", values.is_admin);
      appendIfValid("position", values.position);
      appendIfValid("gender", values.gender);
      appendIfValid("userName", account.user.firstname);
      appendIfValid("userId", account.user.id);

      if (initialData) appendIfValid("id", initialData.id);

      // Employee-specific fields
      if (userType === "employee") {
        appendIfValid("hireDate", values.hireDate);
        appendIfValid("nssf", values.nssf);
        appendIfValid("nhif", values.nhif);
        appendIfValid("county", values.county);
        appendIfValid("address", values.address);
        appendIfValid("leave_days", values.leave_days);
        appendIfValid("payment_rate", values.payment_rate);
        appendIfValid("emp_status", values.emp_status);
        appendIfValid("is_active", values.is_active);
        appendIfValid("nok_firstname", values.nok_firstname);
        appendIfValid("nok_lastname", values.nok_lastname);
        appendIfValid("nok_email", values.nok_email);
        appendIfValid("nok_phone_number", values.nok_phone_number);
        appendIfValid("nok_address", values.nok_address);

        if (passportFile) formData.append("passport", passportFile);
        if (idFile) formData.append("id_card", idFile);
        if (nhifFile) formData.append("nhif_card", nhifFile);
        if (nssfFile) formData.append("nssf_card", nssfFile);
      }

      // Log the FormData content for debugging
      for (let pair of formData.entries()) {
}

      const url = initialData
        ? `/users/update/${initialData.id}`
        : `/users/new`;
      const method = initialData ? "put" : "post";

      const response = await axios({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        message.success(
          response.data.message ||
            `User ${initialData ? "updated" : "created"} successfully`,
        );
        onSuccess();
      } else {
        message.error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("API error:", error);
      message.error(error.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const steps =
    userType === "admin"
      ? ["Personal Details", "Role"]
      : ["Personal Details", "Employment Details", "Next of Kin", "Documents"];

  const renderStepContent = () => {
    if (userType === "admin") {
      return currentStep === 0 ? (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="firstname"
              label="First Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lastname"
              label="Last Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, type: "email" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone_number"
              label="Phone Number"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="id_number"
              label="ID Number"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="mpesa_number" label="Mpesa Number">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="farm" label="Farm" rules={[{ required: true }]}>
              <Select options={farmOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="position"
              label="Position"
              rules={[{ required: true }]}
            >
              <Select options={positionOptions} allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="gender"
              label="Gender"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: "Male", label: "Male" },
                  { value: "Female", label: "Female" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      ) : (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="is_admin"
              label="User Role"
              rules={[{ required: true }]}
            >
              <Select options={roleOptions} />
            </Form.Item>
          </Col>
        </Row>
      );
    }
    // Employee steps
    switch (currentStep) {
      case 0:
        return (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstname"
                label="First Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastname"
                label="Last Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, type: "email" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone_number"
                label="Phone Number"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="id_number"
                label="ID Number"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mpesa_number" label="Mpesa Number">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="farm" label="Farm" rules={[{ required: true }]}>
                <Select options={farmOptions} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="position" label="Position">
                <Select options={positionOptions} allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="gender"
                label="Gender"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: "Male", label: "Male" },
                    { value: "Female", label: "Female" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="hireDate"
                label="Hire Date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        );
      case 1:
        return (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="nssf" label="NSSF" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="nhif" label="NHIF" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="county"
                label="County"
                rules={[{ required: true }]}
              >
                <Select
                  options={counties.map((c) => ({
                    value: c.name,
                    label: c.name,
                  }))}
                  showSearch
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="address"
                label="Address"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="leave_days"
                label="Leave Days"
                rules={[{ required: true }]}
              >
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="payment_rate"
                label="Payment Rate"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="Per Day">Per Day</Option>
                  <Option value="Per Week">Per Week</Option>
                  <Option value="Per Month">Per Month</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="emp_status"
                label="Employment Status"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="Permanent">Permanent</Option>
                  <Option value="Contract">Contract</Option>
                  <Option value="Temporary">Temporary</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Status"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="Active">Active</Option>
                  <Option value="Inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
            {/* <Row gutter={24}> */}
            <Col span={24}>
              <Form.Item
                name="is_admin"
                label="User Role"
                rules={[{ required: true }]}
              >
                <Select options={roleOptions} />
              </Form.Item>
            </Col>
            {/* </Row> */}
          </Row>
        );
      case 2:
        return (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="nok_firstname"
                label="NOK First Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nok_lastname"
                label="NOK Last Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nok_email"
                label="NOK Email"
                rules={[{ required: true, type: "email" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nok_phone_number"
                label="NOK Phone"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="nok_address"
                label="NOK Address"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        );
      case 3:
        return (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Passport Photo" required={!initialData}>
                <Dragger
                  beforeUpload={(file) => {
                    setPassportFile(file);
                    return false;
                  }}
                  maxCount={1}
                  fileList={
                    passportFile ? [{ uid: "1", name: passportFile.name }] : []
                  }
                  onRemove={() => setPassportFile(null)}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p>Click or drag to upload</p>
                </Dragger>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="ID Card Photo" required={!initialData}>
                <Dragger
                  beforeUpload={(file) => {
                    setIdFile(file);
                    return false;
                  }}
                  maxCount={1}
                  fileList={idFile ? [{ uid: "1", name: idFile.name }] : []}
                  onRemove={() => setIdFile(null)}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p>Click or drag to upload</p>
                </Dragger>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="NHIF Card (Optional)">
                <Dragger
                  beforeUpload={(file) => {
                    setNhifFile(file);
                    return false;
                  }}
                  maxCount={1}
                  fileList={nhifFile ? [{ uid: "1", name: nhifFile.name }] : []}
                  onRemove={() => setNhifFile(null)}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p>Click or drag to upload</p>
                </Dragger>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="NSSF Card (Optional)">
                <Dragger
                  beforeUpload={(file) => {
                    setNssfFile(file);
                    return false;
                  }}
                  maxCount={1}
                  fileList={nssfFile ? [{ uid: "1", name: nssfFile.name }] : []}
                  onRemove={() => setNssfFile(null)}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p>Click or drag to upload</p>
                </Dragger>
              </Form.Item>
            </Col>
          </Row>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      title={initialData ? "Edit User" : "Add User"}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            is_active: "Active",
            emp_status: "Permanent",
            payment_rate: "Per Month",
            user_type: "admin",
          }}
        >
          <Form.Item
            name="user_type"
            label="User Type"
            rules={[{ required: true }]}
          >
            <Radio.Group onChange={handleUserTypeChange} value={userType}>
              <Radio value="admin">Admin</Radio>
              <Radio value="employee">Employee</Radio>
            </Radio.Group>
          </Form.Item>

          <Steps current={currentStep} style={{ marginBottom: 24 }}>
            {steps.map((title) => (
              <Step key={title} title={title} />
            ))}
          </Steps>

          {renderStepContent()}

          <div style={{ marginTop: 24, textAlign: "right" }}>
            {currentStep > 0 && (
              <Button style={{ marginRight: 8 }} onClick={prev}>
                Previous
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button type="primary" onClick={next}>
                Next
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button type="primary" htmlType="submit" loading={loading}>
                {initialData ? "Update" : "Submit"}
              </Button>
            )}
            <Button style={{ marginLeft: 8 }} onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default UserForm;
